import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['input', 'submit', 'suggestion', 'startButton', 'stopButton', 'breakButton', 'suggestions', 'messages', 'anchor', 'chatMessage', 'prompts']

  static values = {
    lang: { type: String, default: 'en-US' }
  }

  connect () {
    this.clearInput()
    this.#scrollToBottom()
    this.#initializeRecognition()
    this.#observeMessages()
  }

  handleKeydown (e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()

      if (!this.submitTarget.disabled) {
        this.#autoSubmit()
      }
    }
  }

  startRecording () {
    if (this.recognition) {
      this.recognition.start()
      this.#toggleRecordingButtons(true)
    }
  }

  endRecording () {
    if (this.recognition) {
      this.recognition.stop()
      this.#toggleRecordingButtons(false)
    }
  }

  breakRecording () {
    if (!this.recognition) return

    this.recognition.stop()
    // Have to wait for the recognition to stop before clearing the input (Mobile Safari issue)
    setTimeout(() => { this.clearInput() }, 100)
    this.#toggleRecordingButtons(false)
  }

  applyStarterPropmt (e) {
    const text = e.target.textContent
    this.inputTarget.value = text
    this.submitTarget.disabled = false

    // Since reflection does not have an ai_mentor on this step, we cannot use button_to and reflection object, since for the case of applySuggestion() fn
    // Timeout for Mobile Safari issue
    setTimeout(() => {
      this.#autoSubmit()
    }, 1)
  }

  applySuggestion (e) {
    const text = e.target.textContent
    this.inputTarget.value = text
    this.submitTarget.disabled = false
  }

  onSubmit () {
    this.clearInput()
    this.#hidePrompts()
    this.#disableSubmit()
    // There are 2 ways to send recognition result: by click submitTarget or stopButtonTarget.
    // That's why we need to check if the recognition is active and stop it.
    // Cannot use stopRecording() here because inside is #autoSubmit() fn.
    if (this.recognition) {
      this.recognition.stop()
      this.#toggleRecordingButtons(false)
      this.stopButtonTarget.disabled = true
    }
  }

  toggleSubmit () {
    const isInputEmpty = this.inputTarget.value.trim() === ''

    if (isInputEmpty) {
      this.#disableSubmit()
      this.stopButtonTarget.disabled = true
    } else {
      this.#enableSubmit()
      this.stopButtonTarget.disabled = false
    }
  }

  clearInput () {
    this.inputTarget.value = ''
  }

  #autoSubmit () {
    this.submitTarget.click()
  }

  #disableSubmit () {
    this.submitTarget.disabled = true
  }

  #enableSubmit () {
    this.submitTarget.disabled = false
  }

  #scrollToBottom () {
    if (!this.hasMessagesTarget) return

    this.messagesTarget.scrollTo({
      top: this.anchorTarget.offsetTop,
      behavior: 'auto'
    })
  }

  #hidePrompts () {
    if (!this.hasMessagesTarget) return

    if (this.hasPromptsTarget) {
      this.promptsTarget.remove()
    }
  }

  #initializeRecognition () {
    this.recognition = null

    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      this.recognition = new SpeechRecognition()
      this.recognition.lang = this.langValue
      this.recognition.continuous = true
      this.recognition.interimResults = true // temporarily show results

      this.recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript
        this.inputTarget.value = transcript
        this.submitTarget.disabled = false
        this.toggleSubmit()
      }
    }
  }

  #toggleRecordingButtons (isRecording) {
    if (isRecording) {
      this.startButtonTarget.classList.add('hidden')
      this.stopButtonTarget.classList.remove('hidden')
      this.breakButtonTarget.classList.remove('hidden')
    } else {
      this.startButtonTarget.classList.remove('hidden')
      this.stopButtonTarget.classList.add('hidden')
      this.breakButtonTarget.classList.add('hidden')
    }
  }

  #observeMessages () {
    if (!this.hasMessagesTarget) return

    if (typeof window.MutationObserver !== 'undefined') {
      const observer = new window.MutationObserver(() => {
        if (this.messageTimeout) {
          clearTimeout(this.messageTimeout)
        }
        this.messageTimeout = setTimeout(() => {
          this.#scrollToLastMessage()
        }, 100)
      })

      observer.observe(this.messagesTarget, { childList: true, subtree: true, characterData: true })
    }
  }

  #scrollToLastMessage () {
    if (!this.hasMessagesTarget) return

    const lastMessage = this.chatMessageTargets.at(-1)
    lastMessage.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }
}
